/* eslint-disable @typescript-eslint/no-explicit-any */
import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";

export type EditTabKey =
  | "deductions"
  | "savingsInvestments"
  | "houseProperty"
  | "otherSources"
  | "additional";

export type PossessionStatus = "" | "yes" | "no";
export type PaymentUnit = "INR" | "PERCENT";

export interface DeductionRow {
  id: string;
  label: string;
  deductibleAmount: number;
  exemptionPercentage?: number;
  contributionAmount: string;
}

export interface LetOutProperty {
  id: string;
  slNo: number;
  rentalIncome: string;
  interestPaid: string;
  tdsOnRent: string;
  repairExpenditure: string;
  otherExpenses: string;
  eligibleForDeduction: boolean;
  possessionObtained: PossessionStatus;
}

export interface AdditionalPaymentRow {
  id: string;
  type: string;
  value: string;
  unit: PaymentUnit;
}

export interface DeclarationSnapshot {
  totalIncomeDeductions: DeductionRow[];
  savingsInvestments: DeductionRow[];
  homeLoanInterestPaid: string;
  eligibleForHousingLoanDeduction: boolean;
  selfOccupiedPossession: PossessionStatus;
  letOutProperties: LetOutProperty[];
  otherSourcesValues: Record<string, string>;
  additionalPayments: AdditionalPaymentRow[];
}

export interface TaxLineItem {
  label: string;
  value: number;
}

export interface RegimeView {
  name: string;
  selected: boolean;
  totalAnnualTax: number;
  grossSalary: TaxLineItem[];
  exemptions: TaxLineItem[];
  taxableIncome: TaxLineItem[];
  taxCalculation: TaxLineItem[];
}

// Same field names as the mobile Taxdeclarationedit screen. SAP typos
// (NONFinancia, IncomefromDigitalAsset) are kept so the payload round-trips.
export const OTHER_SOURCES_FIELD_MAP: Record<string, string> = {
  business_profits: "BusinessProfits",
  long_term_capital_gains_n: "LongTermCapitalGainsN",
  long_term_capital_gains_s: "LongTermCapitalGainsS",
  long_term_capital_gains_financial_non_financial: "LongTermCapitalGainsFinancialNONFinancia",
  long_term_capital_gains_slab_rate: "LongTermCapitalGainsSlabRate",
  short_term_capital_gains: "ShortTermCapitalGains",
  short_term_capital_gains_listed_sec: "ShortTermCapitalGainsListedSec",
  short_term_capital_gains_certain_financial: "ShortTermCapitalGainsCertainFinancial",
  income_from_interest: "IncomeFromInterest",
  other_income_unspecified: "OtherIncomeUnspecified",
  income_from_digital_asset: "IncomefromDigitalAsset",
  income_from_online_gaming: "IncomefromOnlineGaming",
  income_from_winnings_lotteries: "IncomefromWinningsLotteries",
  tds_on_winnings_lotteries: "TDSonWinningsLotteries",
  tds_tcs_on_other_income: "TDSTCSonOtherIncome",
};

export const OTHER_SOURCES_INCOME_KEYS = [
  "business_profits",
  "long_term_capital_gains_n",
  "long_term_capital_gains_s",
  "long_term_capital_gains_financial_non_financial",
  "long_term_capital_gains_slab_rate",
  "short_term_capital_gains",
  "short_term_capital_gains_listed_sec",
  "short_term_capital_gains_certain_financial",
  "income_from_interest",
  "other_income_unspecified",
  "income_from_digital_asset",
  "income_from_online_gaming",
  "income_from_winnings_lotteries",
  "tds_on_winnings_lotteries",
];

export const OTHER_SOURCES_TDS_KEYS = ["tds_tcs_on_other_income"];

export const OTHER_SOURCE_LABELS: Record<string, string> = {
  business_profits: "Business Profits",
  long_term_capital_gains_n: "Long Term Capital Gains (N)",
  long_term_capital_gains_s: "Long Term Capital Gains (S)",
  long_term_capital_gains_financial_non_financial: "Long Term Capital Gains (Financial/Non-Financial)",
  long_term_capital_gains_slab_rate: "Long Term Capital Gains (Slab Rate)",
  short_term_capital_gains: "Short Term Capital Gains",
  short_term_capital_gains_listed_sec: "Short Term Capital Gains (Listed Sec)",
  short_term_capital_gains_certain_financial: "Short Term Capital Gains (Certain Financial)",
  income_from_interest: "Income from Interest",
  other_income_unspecified: "Other Income (Unspecified)",
  income_from_digital_asset: "Income from Digital Asset",
  income_from_online_gaming: "Income from Online Gaming",
  income_from_winnings_lotteries: "Income from Winnings / Lotteries",
  tds_on_winnings_lotteries: "TDS on Winnings / Lotteries",
  tds_tcs_on_other_income: "TDS/TCS on Other Income",
};

export const DECL_TABS: { key: EditTabKey; label: string; icon: string }[] = [
  { key: "deductions", label: "Deductions from Total Income", icon: "ti-cash" },
  { key: "savingsInvestments", label: "Deductions for Savings/Investments", icon: "ti-trending-up" },
  { key: "houseProperty", label: "Income/Loss from House Property", icon: "ti-home" },
  { key: "otherSources", label: "Income from Other Sources", icon: "ti-layers-intersect" },
  { key: "additional", label: "Additional Payments / Deductions", icon: "ti-adjustments" },
];

let letOutIdCounter = 0;
let additionalPaymentIdCounter = 0;

export function makeLetOutProperty(slNo: number): LetOutProperty {
  letOutIdCounter += 1;
  return {
    id: `prop_${letOutIdCounter}`,
    slNo,
    rentalIncome: "",
    interestPaid: "",
    tdsOnRent: "",
    repairExpenditure: "",
    otherExpenses: "",
    eligibleForDeduction: false,
    possessionObtained: "",
  };
}

export function makeAdditionalPaymentRow(): AdditionalPaymentRow {
  additionalPaymentIdCounter += 1;
  return {
    id: `addl_${additionalPaymentIdCounter}`,
    type: "",
    value: "0.00",
    unit: "INR",
  };
}

export function getLoggedInEmpId(): string {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return "";
    const parsed = JSON.parse(raw);
    const user = parsed?.details || parsed?.empId || parsed?.usrid
      ? parsed
      : parsed?.data?.data?.user || parsed?.data?.user || parsed?.user || parsed || {};
    return String(user?.details?.empId ?? user?.empId ?? user?.usrid ?? "").trim();
  } catch {
    return "";
  }
}

export function toNumber(v: string | number | undefined): number {
  const n = parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : 0;
}

export function toDisplayAmount(v: string | number | undefined): string {
  return toNumber(v).toFixed(2);
}

export function toSapAmount(v: string | number | undefined): string {
  return toNumber(v).toFixed(3);
}

export function formatAmount(n: number): string {
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatINR(value: number): string {
  const isNegative = value < 0;
  const fixed = Math.abs(value).toFixed(2);
  const [intPart, decPart] = fixed.split(".");
  const lastThree = intPart.slice(-3);
  const otherDigits = intPart.slice(0, -3);
  const grouped =
    (otherDigits ? otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," : "") + lastThree;
  return `${isNegative ? "-" : ""}₹${grouped}.${decPart}`;
}

const fromSapFlag = (v?: string) => String(v ?? "").trim().toUpperCase() === "X";
const toSapFlag = (v: boolean) => (v ? "X" : "");

const fromSapPossession = (v?: string): PossessionStatus => {
  const upper = String(v ?? "").trim().toUpperCase();
  if (upper === "Y" || upper === "1") return "yes";
  if (upper === "N" || upper === "0") return "no";
  return "";
};

const toSapPossession = (v: PossessionStatus) => (v === "yes" ? "Y" : v === "no" ? "N" : "");

function asResults(node: any): any[] {
  if (Array.isArray(node)) return node;
  if (Array.isArray(node?.results)) return node.results;
  return [];
}

function buildInitialAmounts(keys: string[]): Record<string, string> {
  return keys.reduce((acc, key) => {
    acc[key] = "0.00";
    return acc;
  }, {} as Record<string, string>);
}

function collectLayers(res: any): any[] {
  return [res, res?.data, res?.data?.data, res?.data?.data?.data].filter(
    (layer) => layer !== undefined && layer !== null
  );
}

function unwrapResponse(res: any): { ok: boolean; message: string; payload: any } {
  for (const layer of collectLayers(res)) {
    if (!layer || typeof layer !== "object" || Array.isArray(layer)) continue;
    const statusOk = layer.status === 200 || layer.status === 201 || layer.success === true;
    if (statusOk) {
      return {
        ok: true,
        message: String(layer.message || "Success"),
        payload: layer.data !== undefined ? layer.data : layer,
      };
    }
    if (layer.success === false) {
      return {
        ok: false,
        message: String(layer.message || "Something went wrong"),
        payload: layer.data ?? null,
      };
    }
  }
  return {
    ok: false,
    message: String(res?.data?.message || res?.message || "Something went wrong"),
    payload: res?.data?.data ?? res?.data ?? null,
  };
}

function pickPdfContent(payload: any): string {
  const candidates = [
    Array.isArray(payload) ? payload[0] : null,
    payload,
    payload?.results?.[0],
    Array.isArray(payload?.data) ? payload.data[0] : payload?.data,
  ];
  for (const item of candidates) {
    const content = item?.Content ?? item?.content ?? item?.FileContent;
    if (typeof content === "string" && content.trim()) return content;
  }
  return "";
}

function pickCompare(payload: any): any {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload[0] || null;
  if (
    payload.GrossSalaryNew !== undefined ||
    payload.GrossSalaryOld !== undefined ||
    payload.TotalAnnualTaxNew !== undefined ||
    payload.TotalAnnualTaxOld !== undefined
  ) {
    return payload;
  }
  if (Array.isArray(payload.results) && payload.results[0]) return payload.results[0];
  if (payload.data) return pickCompare(payload.data);
  return payload;
}

function pickEditEntity(payload: any): any {
  const body = payload?.data ?? payload;
  const data = Array.isArray(body) ? body[0] : body;
  if (!data || typeof data !== "object") return null;
  if (
    data.To_DTotal_Income ||
    data.To_DSavings_Investments ||
    data.To_Income_Other ||
    data.To_Loss_Self_Occupied ||
    data.To_Income_Loss ||
    data.To_Additional
  ) {
    return data;
  }
  if (Array.isArray(data.results) && data.results[0]) return pickEditEntity(data.results[0]);
  if (data.data) return pickEditEntity(data.data);
  return data;
}

export function mapTaxDeclarationEditResponse(data: any): DeclarationSnapshot {
  const initialOtherSourcesValues = buildInitialAmounts([
    ...OTHER_SOURCES_INCOME_KEYS,
    ...OTHER_SOURCES_TDS_KEYS,
  ]);
  const totalIncomeRows: DeductionRow[] = asResults(data?.To_DTotal_Income).map((r: any, idx: number) => ({
    id: `ti_${idx}`,
    label: String(r?.Contributions ?? ""),
    deductibleAmount: toNumber(r?.DeductibleAmount),
    exemptionPercentage: toNumber(r?.ExemptionPercentage),
    contributionAmount: toDisplayAmount(r?.ContributionAmount),
  }));
  const savingsRows: DeductionRow[] = asResults(data?.To_DSavings_Investments).map((r: any, idx: number) => ({
    id: `si_${idx}`,
    label: String(r?.Contributions ?? ""),
    deductibleAmount: toNumber(r?.DeductibleAmount),
    contributionAmount: toDisplayAmount(r?.ContributionAmount),
  }));
  const selfOccupied = asResults(data?.To_Loss_Self_Occupied)[0] ?? {};
  const letOutRows: LetOutProperty[] = asResults(data?.To_Income_Loss).map((r: any, idx: number) => ({
    id: `prop_${idx}`,
    slNo: Number(r?.SlNo) || idx + 1,
    rentalIncome: toDisplayAmount(r?.RentalIncome),
    interestPaid: toDisplayAmount(r?.InterestPaid),
    tdsOnRent: toDisplayAmount(r?.TDSonRent),
    repairExpenditure: toDisplayAmount(r?.RepairExpenditure),
    otherExpenses: toDisplayAmount(r?.OtherExpenses),
    eligibleForDeduction: fromSapFlag(r?.EligibleforHousingLoanInterestDeduction),
    possessionObtained: fromSapPossession(r?.PossessionObtained),
  }));
  const otherSourcesRow = asResults(data?.To_Income_Other)[0] ?? data?.To_Income_Other ?? {};
  const otherSourcesMapped: Record<string, string> = { ...initialOtherSourcesValues };
  Object.keys(OTHER_SOURCES_FIELD_MAP).forEach((key) => {
    otherSourcesMapped[key] = toDisplayAmount(otherSourcesRow?.[OTHER_SOURCES_FIELD_MAP[key]]);
  });
  const additionalRows: AdditionalPaymentRow[] = asResults(data?.To_Additional)
    .filter((r: any) => String(r?.AdditionalPaymentDeduction ?? "").trim())
    .map((r: any) => {
      additionalPaymentIdCounter += 1;
      return {
        id: `addl_${additionalPaymentIdCounter}`,
        type: String(r?.AdditionalPaymentDeduction ?? ""),
        value: toDisplayAmount(r?.Value),
        unit: (String(r?.Unit ?? "").toUpperCase() === "PERCENT" ? "PERCENT" : "INR") as PaymentUnit,
      };
    });

  return {
    totalIncomeDeductions: totalIncomeRows,
    savingsInvestments: savingsRows,
    homeLoanInterestPaid: toDisplayAmount(selfOccupied?.HomeLoanInterestPaid),
    eligibleForHousingLoanDeduction: fromSapFlag(selfOccupied?.EligibleforHousingLoanInterestDeduction),
    selfOccupiedPossession: fromSapPossession(selfOccupied?.PossessionObtained),
    letOutProperties: letOutRows,
    otherSourcesValues: otherSourcesMapped,
    additionalPayments: additionalRows,
  };
}

export function buildSavePayload(empId: string, snapshot: DeclarationSnapshot) {
  const otherSourcesRow: Record<string, string> = { EmpID: empId };
  Object.keys(OTHER_SOURCES_FIELD_MAP).forEach((key) => {
    otherSourcesRow[OTHER_SOURCES_FIELD_MAP[key]] = toSapAmount(snapshot.otherSourcesValues[key]);
  });

  return {
    Action: "C",
    EmpID: empId,
    Message: "",
    To_DTotal_Income: {
      results: snapshot.totalIncomeDeductions.map((row) => ({
        EmpID: empId,
        Contributions: row.label,
        DeductibleAmount: toSapAmount(row.deductibleAmount),
        ExemptionPercentage: toSapAmount(row.exemptionPercentage ?? 0),
        ContributionAmount: toSapAmount(row.contributionAmount),
      })),
    },
    To_DSavings_Investments: {
      results: snapshot.savingsInvestments.map((row) => ({
        EmpID: empId,
        Contributions: row.label,
        DeductibleAmount: toSapAmount(row.deductibleAmount),
        ContributionAmount: toSapAmount(row.contributionAmount),
      })),
    },
    To_Income_Other: { results: [otherSourcesRow] },
    To_Additional: {
      results:
        snapshot.additionalPayments.length > 0
          ? snapshot.additionalPayments.map((p) => ({
              EmpID: empId,
              AdditionalPaymentDeduction: p.type,
              Value: toSapAmount(p.value),
              Unit: p.unit,
            }))
          : [{ EmpID: empId, AdditionalPaymentDeduction: "", Value: "0.000", Unit: "" }],
    },
    To_Loss_Self_Occupied: {
      results: [
        {
          EmpID: empId,
          HomeLoanInterestPaid: toSapAmount(snapshot.homeLoanInterestPaid),
          EligibleforHousingLoanInterestDeduction: toSapFlag(snapshot.eligibleForHousingLoanDeduction),
          PossessionObtained: toSapPossession(snapshot.selfOccupiedPossession),
        },
      ],
    },
    To_Income_Loss: {
      results: snapshot.letOutProperties.map((p, idx) => ({
        EmpID: empId,
        SlNo: p.slNo || idx + 1,
        RentalIncome: toSapAmount(p.rentalIncome),
        InterestPaid: toSapAmount(p.interestPaid),
        TDSonRent: toSapAmount(p.tdsOnRent),
        RepairExpenditure: toSapAmount(p.repairExpenditure),
        OtherExpenses: toSapAmount(p.otherExpenses),
        EligibleforHousingLoanInterestDeduction: toSapFlag(p.eligibleForDeduction),
        PossessionObtained: toSapPossession(p.possessionObtained),
      })),
    },
  };
}

function buildRegime(data: any, suffix: "Old" | "New", aggregateLabel: string): Omit<RegimeView, "name" | "selected"> {
  const num = (key: string): number => parseFloat(data?.[`${key}${suffix}`]) || 0;
  return {
    totalAnnualTax: num("TotalAnnualTax"),
    grossSalary: [
      { label: "1. Salary as per Provisions u/s 17(1)", value: num("SalaryAsPerProvisions") },
      { label: "2. Value of Perquisites u/s 17(2)", value: num("ValueofPerquisites") },
      { label: "3. Profits in Lieu of Salary u/s 17(3)", value: num("ProfitsInLieuOfSalary") },
      { label: "4. Employer Annual NPS Contribution", value: num("EmployerAnnualNPSContribution") },
      { label: "5. Gross Salary (1+2+3+4)", value: num("GrossSalary") },
    ],
    exemptions: [
      { label: "6. Exemptions u/s 10", value: num("Exemptions") },
      { label: aggregateLabel, value: num("AggregateOfDeductions") },
      { label: "8. Income/Loss from House Property", value: num("IncomeLossFromHouseProperty") },
    ],
    taxableIncome: [
      { label: "9. Income from Other Sources", value: num("IncomeFromOtherSources") },
      { label: "10. Gross Total Income", value: num("GrossTotalIncome") },
      { label: "11. Deductible Amount Chapter VI-A", value: num("DeductibleAmountChapter") },
      { label: "12. Total Taxable Income (10 - 11)", value: num("TotalTaxableIncome") },
    ],
    taxCalculation: [
      { label: "13. Tax Credit", value: num("TaxCredit") },
      { label: "14. Health and Education Cess", value: num("HealthAndEducationCess") },
      { label: "15. Surcharge", value: num("Surcharge") },
      { label: "16. Annual Tax Payable", value: num("AnnualTaxPayable") },
      { label: "17. Relief u/s 89", value: num("Relief") },
      { label: "18. Total Annual Tax (16 - 17)", value: num("TTotalAnnualTax") },
    ],
  };
}

export function buildRegimes(data: any): Record<"old" | "new", RegimeView> {
  return {
    old: {
      name: "Old Tax Regime",
      selected: false,
      ...buildRegime(data, "Old", "7. Aggregate of 4(a) and 4(b)"),
    },
    new: {
      name: "New Tax Regime",
      selected: true,
      ...buildRegime(data, "New", "7. Aggregate of Deductions u/s 16"),
    },
  };
}

export function emptyOtherSources(): Record<string, string> {
  return buildInitialAmounts([...OTHER_SOURCES_INCOME_KEYS, ...OTHER_SOURCES_TDS_KEYS]);
}

export async function fetchTaxSimplifierPdf(empId: string): Promise<string> {
  const res: any = await Api.post(API_URLS.TAX_SIMPLIFIER.FETCH, { empId });
  const result = unwrapResponse(res);
  const content = pickPdfContent(result.payload);
  if (!result.ok || !content) {
    throw new Error(result.message || "Something went wrong");
  }
  return content;
}

export async function fetchTaxCompare(empId: string): Promise<any> {
  const res: any = await Api.post(API_URLS.TAX_CARD.SIMPLIFIER_COMPARE, { empId });
  const result = unwrapResponse(res);
  const data = pickCompare(result.payload);
  if (!result.ok || !data) {
    throw new Error(result.message || "Something went wrong");
  }
  return data;
}

export async function fetchTaxDeclaration(empId: string): Promise<DeclarationSnapshot> {
  const res: any = await Api.post(API_URLS.TAX_SIMPLIFIER.EDIT_FETCH, { empId });
  const result = unwrapResponse(res);
  const entity = pickEditEntity(result.payload);
  if (!result.ok || !entity) {
    throw new Error(result.message || "Something went wrong");
  }
  return mapTaxDeclarationEditResponse(entity);
}

export async function saveTaxDeclaration(
  empId: string,
  snapshot: DeclarationSnapshot
): Promise<{ message: string; snapshot: DeclarationSnapshot | null }> {
  const payload = buildSavePayload(empId, snapshot);
  const res: any = await Api.post(API_URLS.TAX_SIMPLIFIER.EDIT_SAVE, payload);
  const result = unwrapResponse(res);
  if (!result.ok) {
    throw new Error(result.message || "Something went wrong");
  }
  const entity = pickEditEntity(result.payload);
  const saved = entity ? mapTaxDeclarationEditResponse(entity) : null;
  const hasRows =
    !!saved &&
    (saved.totalIncomeDeductions.length > 0 ||
      saved.savingsInvestments.length > 0 ||
      saved.letOutProperties.length > 0 ||
      saved.additionalPayments.length > 0 ||
      toNumber(saved.homeLoanInterestPaid) > 0);
  return {
    message: result.message || "Changes saved successfully",
    snapshot: hasRows ? saved : snapshot,
  };
}

export function downloadBase64Pdf(base64: string, fileName: string): void {
  let cleaned = base64.trim();
  const marker = cleaned.indexOf("base64,");
  if (marker !== -1) cleaned = cleaned.slice(marker + "base64,".length);
  cleaned = cleaned.replace(/\s/g, "");
  const byteChars = atob(cleaned);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
